import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import Dashboard from './pages/Dashboard';
import Tickets from './pages/Tickets';
import Requests from './pages/Requests';
import Search from './pages/Search';
import CustomerDetail from './pages/CustomerDetail';
import Docs from './pages/Docs';
import Assistant from './pages/Assistant';
import Products from './pages/Products';
import EmailConfig from './pages/EmailConfig';
import Infrastructure from './pages/Infrastructure';
import Bleed from './pages/Bleed';
import Deletions from './pages/Deletions';
import Referrals from './pages/Referrals';
import Sales from './pages/Sales';
import Finance from './pages/Finance';
import Console from './components/Console';
import LiveChatDock from './components/LiveChatDock';
import OTPModal from './components/OTPModal';
import AccountConfirmModal from './components/AccountConfirmModal';
import { useViewContext } from './lib/viewContext';
import { isUnlocked, unlockAccount, isViewOnlyUnlock, subscribeAccountSession } from './lib/accountSession';
import { listOpenAccounts, openAccount, closeAccount, updateOpenAccount, subscribeOpenAccounts, accountColor } from './lib/openAccounts';
import { noteAccountOpened, noteAccountActivity, forgetAccountActivity, idleFor, createAccountWriteGate } from './lib/accountIdle';
import { fetchWhoami, fetchRequests, getHeaders } from './api';
import { setStaff, isReadOnlyStaff, isViewer, roleLabel, installViewOnlyFetchGuard, VIEW_ONLY_MESSAGE } from './lib/staffAccess';
import { noteAccountLock } from './lib/accountLockWatch';
import './App.css';

const API = import.meta.env.PROD ? '' : 'https://crm-api.squish.co';

// What a shift actually runs on. Three items don't need a section label.
// 'search' (Customer Lookup + CustomerDetail) is still a page — it is where
// the Dashboard's search box, "+ New Account" and every account click land —
// but it no longer needs a nav entry of its own.
// Tickets are customers' Salesforce cases; Requests are the team's own asks,
// filed by mentioning @genius in Slack. Different queues, different words.
const NAV_ITEMS = [
  { id: 'dashboard', label: 'Dashboard', icon: '▦' },
  { id: 'tickets',   label: 'Tickets',   icon: '◉' },
  { id: 'requests',  label: 'Requests',  icon: '◈' },
];

// Owner-only surfaces, behind one disclosure pinned to the bottom of the nav.
// These were four labelled sections outnumbering the daily two seven-to-two,
// so the sidebar read as a config panel with a CRM attached.
// Infrastructure reads from infrastructure_state/current that the
// squish-backend capacity-snapshot cron parks every 4h.
// `roles`: who sees the entry. Absent = every staffer (the backend still gates
// writes). Sales is admin-only in both places — it is every rep's number, and
// the Dashboard strip already shows each rep their own.
// `needs`: a whoami flag that must ALSO be exactly true. Finance is Casey's
// alone, not every admin's: squish-crm-backend gates /api/finance/* on the
// admin role AND an email allowlist, and says which with whoami's `finance`.
const ADMIN_ITEMS = [
  { id: 'sales',          label: 'Sales',          icon: '$', roles: ['admin'] },
  { id: 'finance',        label: 'Finance',        icon: '∑', roles: ['admin'], needs: 'finance' },
  // Approving erases a customer for good; admin-only here and on both server routes.
  { id: 'deletions',      label: 'Deletions',      icon: '⌫', roles: ['admin'] },
  { id: 'bleed',          label: 'Bleed',          icon: '⚠' },
  { id: 'referrals',      label: 'Referrals',      icon: '⇄' },
  { id: 'infrastructure', label: 'Infrastructure', icon: '▤' },
  { id: 'docs',           label: 'Doc Dash',       icon: '◫' },
  { id: 'assistant',      label: 'Assistant',      icon: '⚡' },
  { id: 'email',          label: 'Email',          icon: '✉' },
  { id: 'products',       label: 'Products',       icon: '☷' },
];
const ADMIN_IDS = new Set(ADMIN_ITEMS.map(i => i.id));

// Does this role get to see this page? The nav filter below answers the same
// question for the sidebar; routing has to ask it too, because `active` is
// seeded from window.location.hash and typing #sales is not going through the
// nav. Absent `roles` = every staffer by design (the backend gates the writes),
// so in practice this restricts Sales, Deletions and Finance and nothing else
// today. `me` is null until whoami resolves — callers must treat that as
// "not yet known", never as "allowed". The sidebar filter calls this too, so
// the nav and the router cannot disagree about who sees a page.
function canSeePage(pageId, me) {
  if (!ADMIN_IDS.has(pageId)) return true;
  const item = ADMIN_ITEMS.find(i => i.id === pageId);
  if (item?.needs && me?.[item.needs] !== true) return false;
  if (!item?.roles) return true;
  return item.roles.includes(me?.role);
}

// Human names for the ~ console's breadcrumb ("Customer Lookup › Acme Corp").
const PAGE_LABELS = {
  search: 'Customer Lookup', tickets: 'Tickets', requests: 'Requests', dashboard: 'Dashboard',
  sales: 'Sales', bleed: 'Bleed', referrals: 'Referrals', docs: 'Doc Dash',
  assistant: 'Assistant', email: 'Email config', products: 'Products',
  infrastructure: 'Infrastructure', deletions: 'Account deletions', finance: 'Finance',
};

const VALID_PAGES = new Set(['search', 'tickets', 'requests', 'dashboard', 'sales', 'finance', 'bleed', 'referrals', 'docs', 'assistant', 'email', 'products', 'infrastructure', 'deletions']);

// An open account is a route of its own, #account/<Salesforce Id>, so a reload
// lands back on it and the browser's Back button steps between accounts and
// pages. The route string doubles as the hash.
const ACCOUNT_ROUTE = /^account\/([A-Za-z0-9]{15,18})$/;
const accountRoute = (id) => `account/${id}`;
const accountIdOf = (route) => ACCOUNT_ROUTE.exec(route || '')?.[1] || null;

// Below this the sidebar becomes a drawer behind a top bar. App.css carries the
// same breakpoint; the JS copy is what keeps the rail from rendering collapsed
// (icons only) inside a drawer that has the room for labels.
const MOBILE_QUERY = '(max-width: 768px)';

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mq = window.matchMedia(query);
    const on = () => setMatches(mq.matches);
    on();
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [query]);
  return matches;
}

function routeFromHash() {
  const raw = window.location.hash.replace(/^#/, '');
  if (accountIdOf(raw)) return raw;
  // No hash → Dashboard: the Today column is the point of opening the CRM.
  return VALID_PAGES.has(raw) ? raw : 'dashboard';
}

export default function App() {
  // A locked #account/<id> starts on the Dashboard; the mount effect below
  // puts the OTP modal over it.
  const [active, setActiveRaw] = useState(() => {
    const route = routeFromHash();
    const id = accountIdOf(route);
    return id && !isUnlocked(id) ? 'dashboard' : route;
  });
  // The Administration flyout. A popover, so it starts shut every load and is
  // not persisted — the Administration row carries the active state instead,
  // which is what a #products deep link needs to look right.
  const [adminOpen, setAdminOpen] = useState(false);
  const adminRef = useRef(null);
  const setActive = (route) => {
    setActiveRaw(route);
    if (window.location.hash !== `#${route}`) window.location.hash = route;
  };
  const [autoCreate, setAutoCreate]               = useState(false);
  const [otpTarget, setOtpTarget]                 = useState(null); // { id, name, meta } | null
  const [openList, setOpenList]                   = useState(listOpenAccounts);
  const [collapsedPref, setCollapsed]             = useState(false);
  const isMobile                                  = useMediaQuery(MOBILE_QUERY);
  const [navOpen, setNavOpen]                     = useState(false);
  // A phone gets the full drawer, never the icon rail.
  const collapsed = collapsedPref && !isMobile;
  const [bleedCount, setBleedCount]               = useState(0);
  const [requestsOpen, setRequestsOpen]           = useState(0);
  const [me, setMe]                               = useState(null);
  // Light/dark theme — dark is the default look; choice persists per browser.
  const [theme, setTheme] = useState(() => localStorage.getItem('crm_theme') === 'light' ? 'light' : 'dark');

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('crm_theme', theme);
  }, [theme]);

  // Popover dismissal. Only bound while open, so the usual case costs nothing.
  useEffect(() => {
    if (!adminOpen) return;
    const onDown = (e) => { if (!adminRef.current?.contains(e.target)) setAdminOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setAdminOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [adminOpen]);

  // Poll the bleed report's actionable count for the nav badge (Facebook-style).
  // Best-effort — a failed poll just leaves the last count.
  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const res = await fetch(`${API}/api/billing/bleed-report`, { headers: getHeaders() });
        if (!res.ok) return;
        const data = await res.json();
        if (alive) setBleedCount(Number(data?.actionableCount) || 0);
      } catch { /* badge is best-effort */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // The same badge for Requests: cards nobody has picked up yet (counts.open).
  // Polled here, not in the page, so it shows from every page. The page hands
  // back the counts from each of its own loads — one follows every pick-up,
  // release, close and reopen — so the badge does not sit a minute behind the
  // list beside it. A count that is missing leaves the last one standing
  // rather than reading as "nothing waiting".
  const noteRequestCounts = useCallback((counts) => {
    const open = Number(counts?.open);
    if (Number.isFinite(open) && open >= 0) setRequestsOpen(open);
  }, []);
  useEffect(() => {
    let alive = true;
    const fetchCount = async () => {
      try {
        const data = await fetchRequests();
        if (alive) noteRequestCounts(data?.counts);
      } catch { /* badge is best-effort */ }
    };
    fetchCount();
    const id = setInterval(fetchCount, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [noteRequestCounts]);

  useEffect(() => {
    fetchWhoami().then(setMe).catch(() => setMe(null));
  }, []);

  // View-only staff (a company login with no Salesforce user) and anyone else the
  // server says may only read. The server refuses their writes whatever this
  // shows; publishing the answer here is what lets App.css remove the controls
  // marked data-write, and lets the fetch backstop below explain a write this
  // UI did not think to hide instead of surfacing a bare 403.
  const readOnly = isReadOnlyStaff(me);
  const [blockedNotice, setBlockedNotice] = useState(false);
  useEffect(() => { setStaff(me); }, [me]);

  // The "right account?" check (lib/accountIdle). The fetch gate is installed
  // once, so it reads the account on screen through a ref; the dialog's answer
  // resolves the promise the held writes are waiting on.
  const onScreenRef = useRef(null);
  const confirmRef = useRef(null);                 // { id, resolve } while asking
  const [confirmAsk, setConfirmAsk] = useState(null);
  const answerConfirm = useCallback((ok) => {
    const ask = confirmRef.current;
    confirmRef.current = null;
    setConfirmAsk(null);
    ask?.resolve(ok);
  }, []);
  const confirmYes = useCallback(() => answerConfirm(true), [answerConfirm]);
  const confirmNo = useCallback(() => answerConfirm(false), [answerConfirm]);

  // Any input while an account is on screen is time spent in that account.
  // Capture phase, so it is recorded before the click it belongs to runs.
  useEffect(() => {
    const note = () => { if (onScreenRef.current) noteAccountActivity(onScreenRef.current); };
    const kinds = ['pointerdown', 'keydown', 'wheel', 'touchstart'];
    for (const k of kinds) document.addEventListener(k, note, { capture: true, passive: true });
    return () => { for (const k of kinds) document.removeEventListener(k, note, { capture: true }); };
  }, []);

  useEffect(() => {
    installViewOnlyFetchGuard({
      gate: createAccountWriteGate({
        onScreen: () => onScreenRef.current,
        ask: (id) => new Promise((resolve) => {
          confirmRef.current = { id, resolve };
          setConfirmAsk({ id, idleMs: idleFor(id) });
        }),
      }),
      onBlocked: () => setBlockedNotice(true),
      // An unlock that expired under an open account — see lib/accountLockWatch.
      onResponse: (res, { url, method }) => noteAccountLock(res, url, {
        method,
        onLocked: (id) => window.dispatchEvent(new CustomEvent('crm:account-locked', { detail: { id } })),
      }),
    });
  }, []);
  useEffect(() => {
    if (!blockedNotice) return;
    const id = setTimeout(() => setBlockedNotice(false), 5000);
    return () => clearTimeout(id);
  }, [blockedNotice]);

  // Which page we actually render. `active` can name a role-restricted page
  // without the nav ever offering it — #sales in the address bar seeds `active`
  // directly — so the role rule is applied here as well as in the nav filter.
  // Held at Dashboard until whoami resolves rather than rendering optimistically:
  // mounting the page is what fires its admin-only fetch, so an optimistic
  // render is a guaranteed 403 for the staffer who is allowed in a moment and
  // a wasted call for the one who is not.
  const permittedActive = canSeePage(active, me) ? active : 'dashboard';

  // Once the role is known, correct `active` itself so the hash, the nav
  // highlight and the view context stop disagreeing with what is on screen.
  useEffect(() => {
    if (me && !canSeePage(active, me)) setActive('dashboard');
  }, [me, active]);

  const activeAccountId = accountIdOf(permittedActive);
  onScreenRef.current = activeAccountId;

  // Base layer of the ~ console's view context: which page is open and who is
  // driving. Record-level pages (CustomerDetail, the case drawer) stack on top.
  useViewContext({
    label: activeAccountId ? 'Account' : (PAGE_LABELS[permittedActive] || permittedActive),
    page: activeAccountId ? 'account' : permittedActive,
    staffName: me?.name || null,
    staffEmail: me?.email || null,
    staffRole: me?.role || null,
  }, 0);

  const displayName = me?.name || me?.email || 'Signed in';
  const displayRole = roleLabel(me?.role, me);
  const avatarInitial = (me?.name || me?.email || '?').trim().charAt(0).toUpperCase();

  // ─── Open account tabs ────────────────────────────────────────────────────
  // Every way into an account — a search result, a Dashboard row, the Today
  // rail, Sales, a new account, a pasted #account/<id> — lands here.
  //
  // The OTP consent boundary: an account opens only after someone with access
  // to the customer's own inbox reads back a code. One unlock per account, held
  // in sessionStorage (lib/accountSession), so it is not a prompt per screen,
  // per reload, or per switch between open tabs. If the backend is still
  // bypassing OTP it admits the request anyway and the code costs nothing — but
  // the client never assumes that answer (it used to, and would have 401'd
  // every click the day the backend switch flipped back on).
  const handleNavigateToAccount = (accountId, meta = {}) => {
    if (!accountId) return;
    const isNew = !listOpenAccounts().some(e => e.id === accountId);
    if (openAccount(accountId, meta)) {
      if (isNew) noteAccountOpened(accountId);
      setActive(accountRoute(accountId));
      return;
    }
    setOtpTarget({ id: accountId, name: meta.name || 'this account', meta });
  };

  const handleOTPVerified = (token, opts = {}) => {
    unlockAccount(otpTarget.id, token, { readOnly: opts.readOnly === true });
    // Reading back the customer's code is as deliberate as it gets: a fresh
    // start for the idle check too.
    noteAccountOpened(otpTarget.id);
    handleNavigateToAccount(otpTarget.id, otpTarget.meta);
    setOtpTarget(null);
  };

  // Cancelled: if a typed or Back-buttoned #account/<id> raised the modal, put
  // the address bar back to what is actually on screen.
  const handleOTPCancel = () => {
    setOtpTarget(null);
    if (window.location.hash !== `#${active}`) window.history.replaceState(null, '', `#${active}`);
  };

  useEffect(() => subscribeOpenAccounts(() => setOpenList(listOpenAccounts())), []);

  // An account a LICENSED staffer opened view-only: they can see it and cannot
  // change it until they unlock it properly. Re-render when an unlock changes,
  // so the controls come back the moment the customer's code is verified.
  const [, bumpUnlocks] = useState(0);
  useEffect(() => subscribeAccountSession(() => bumpUnlocks(n => n + 1)), []);
  const unlockForChanges = (id) => {
    const entry = listOpenAccounts().find(a => a.id === id);
    setOtpTarget({ id, name: entry?.name || 'this account', meta: entry ? { name: entry.name, number: entry.number } : {} });
  };

  // The address bar drives the app: a reload, a pasted link, Back/Forward.
  // setActive writes the hash too, so this also fires for in-app clicks — a
  // no-op there, since the route is already set.
  useEffect(() => {
    const follow = () => {
      const route = routeFromHash();
      const id = accountIdOf(route);
      if (id) handleNavigateToAccount(id);
      else setActiveRaw(route);
    };
    follow();
    window.addEventListener('hashchange', follow);
    return () => window.removeEventListener('hashchange', follow);
  }, []);

  // The phone drawer shuts on any nav or tab tap (in the handlers, since a tap
  // on the page already showing changes no route), on a route change from
  // anywhere else, on Escape, and when the window grows past phone width.
  useEffect(() => { setNavOpen(false); }, [permittedActive]);
  useEffect(() => { if (!isMobile) setNavOpen(false); }, [isMobile]);
  useEffect(() => {
    if (!navOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setNavOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen]);

  // Where closing the last open account returns to.
  const lastPageRef = useRef('dashboard');
  useEffect(() => { if (!activeAccountId) lastPageRef.current = permittedActive; }, [permittedActive]);

  // Open accounts stay mounted once visited, hidden when not on screen, so a
  // switch is instant and keeps the account's own sub-tab and half-written
  // forms. Visited-only: restoring eight tabs after a reload must not fire
  // eight sets of account fetches before anyone looks at them. The active one
  // renders even before it reaches the list, so there is no blank frame.
  const visitedRef = useRef(new Set());
  if (activeAccountId) visitedRef.current.add(activeAccountId);
  const mountedIds = openList.map(e => e.id).filter(id => visitedRef.current.has(id));
  if (activeAccountId && !mountedIds.includes(activeAccountId)) mountedIds.push(activeAccountId);

  const closeTab = (id) => {
    if (id === activeAccountId) {
      const idx = openList.findIndex(e => e.id === id);
      const neighbour = openList[idx + 1] || openList[idx - 1];
      setActive(neighbour ? accountRoute(neighbour.id) : lastPageRef.current);
    }
    visitedRef.current.delete(id);
    forgetAccountActivity(id);
    closeAccount(id);
  };

  // The unlock for an open account ran out (1h for a view-only open or an
  // override, 12h for a customer code). Its tab is closed — every panel on it
  // is now a failed load — and the modal is raised to open it again, which
  // remounts the account fresh.
  const closeTabRef = useRef(closeTab);
  closeTabRef.current = closeTab;
  useEffect(() => {
    const onLocked = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      const entry = listOpenAccounts().find(a => a.id === id);
      closeTabRef.current(id);
      setOtpTarget({ id, name: entry?.name || 'this account', meta: entry ? { name: entry.name, number: entry.number } : {} });
    };
    window.addEventListener('crm:account-locked', onLocked);
    return () => window.removeEventListener('crm:account-locked', onLocked);
  }, []);

  // Each open account keeps its own scroll position; pages start at the top.
  const scrollByRoute = useRef({});
  const routeRef = useRef(permittedActive);
  useEffect(() => {
    const onScroll = () => { scrollByRoute.current[routeRef.current] = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useLayoutEffect(() => {
    routeRef.current = permittedActive;
    window.scrollTo(0, activeAccountId ? (scrollByRoute.current[permittedActive] || 0) : 0);
  }, [permittedActive]);

  // Cross-page ticket navigation. Dashboard, CustomerDetail's Recent Activity,
  // anywhere else that surfaces case rows uses this to open a case in the
  // existing Tickets page modal instead of trying to handle the case in-place.
  // Mirrors the customerAccountId/jumpToAccountId pattern above.
  const [pendingCaseId, setPendingCaseId] = useState(null);
  const handleNavigateToTicket = (caseId) => {
    if (!caseId) return;
    setPendingCaseId(caseId);
    setActive('tickets');
  };

  // Dashboard, Today rail and Sales hand over a Salesforce Account-ish object.
  const pickCustomer = (acct) => acct?.Id && handleNavigateToAccount(acct.Id, { name: acct.Name, number: acct.AccountNumber });

  const pages = {
    search: <Search
              autoCreate={autoCreate}
              onAutoCreateDone={() => setAutoCreate(false)}
              onOpenAccount={handleNavigateToAccount}
            />,
    dashboard: <Dashboard
                 onPickCustomer={pickCustomer}
                 onNavigateToTicket={handleNavigateToTicket}
               />,
    tickets:   <Tickets
                 onNavigateToAccount={handleNavigateToAccount}
                 pendingCaseId={pendingCaseId}
                 onPendingCaseHandled={() => setPendingCaseId(null)}
               />,
    requests:  <Requests me={me} onCounts={noteRequestCounts} />,
    docs:      <Docs />,
    assistant: <Assistant />,
    email:     <EmailConfig />,
    products:  <Products />,
    infrastructure: <Infrastructure />,
    bleed:     <Bleed />,
    referrals: <Referrals />,
    sales:     <Sales onPickCustomer={pickCustomer} />,
    finance:   <Finance />,
    deletions: <Deletions />,
  };

  // Admin-only entries hide for everyone else. Before `me` resolves nothing is
  // hidden-then-shown; the panel is closed on load anyway.
  const visibleAdminItems = ADMIN_ITEMS.filter(item => canSeePage(item.id, me));

  const sidebarW = collapsed ? 64 : 220;
  const activeEntry = activeAccountId ? openList.find(e => e.id === activeAccountId) : null;
  const activeColor = activeEntry ? accountColor(activeEntry.color) : null;

  const badgeStyle = {
    marginLeft: 'auto', minWidth: 18, height: 18, padding: '0 5px',
    borderRadius: 9, background: 'var(--accent2)', color: '#fff',
    fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
  };
  const dotStyle = {
    position: 'absolute', top: 6, right: 12, width: 8, height: 8,
    borderRadius: 4, background: 'var(--accent2)',
  };

  // `inPanel` items always render full-width with their label: the flyout has
  // its own 200px of room, so a collapsed sidebar must not shrink them.
  const navButton = (item, { inPanel = false } = {}) => {
    const icons = collapsed && !inPanel;
    const badgeCount = item.id === 'bleed' ? bleedCount : item.id === 'requests' ? requestsOpen : 0;
    return (
      <button
        key={item.id}
        role={inPanel ? 'menuitem' : undefined}
        className={`nav-item ${permittedActive === item.id ? 'active' : ''}`}
        onClick={() => { setActive(item.id); setNavOpen(false); if (inPanel) setAdminOpen(false); }}
        title={icons ? item.label : undefined}
        style={{
          position: 'relative',
          justifyContent: icons ? 'center' : 'flex-start',
          padding: icons ? '10px 0' : '10px 14px',
        }}
      >
        <span className="nav-icon">{item.icon}</span>
        {!icons && <span className="nav-label">{item.label}</span>}
        {badgeCount > 0 && (
          icons ? <span style={dotStyle} /> : <span style={badgeStyle}>{badgeCount}</span>
        )}
      </button>
    );
  };

  return (
    <div className="app">
      {/* The ~ console — global agentic surface, toggled with ` */}
      <Console />
      {/* Live chat — shoppers who ask the assistant for a person. Hidden for
          roles that cannot take chats; see components/LiveChatDock.jsx. */}
      <LiveChatDock me={me} onOpenCase={handleNavigateToTicket} />
      {/* Phone: a top bar stands in for the sidebar, which becomes a drawer.
          The open accounts ride along on the right so switching customers is
          one tap, not open-drawer-then-tap. */}
      {isMobile && (
        <header className="mobile-topbar" style={activeColor ? { '--acct': activeColor } : undefined}>
          <button
            className="mobile-menu-btn"
            onClick={() => setNavOpen(true)}
            aria-label="Open menu"
            aria-expanded={navOpen}
          >
            ☰
            {(bleedCount > 0 || requestsOpen > 0) && <span className="mobile-menu-dot" />}
          </button>
          <div className="mobile-topbar-title">
            {activeEntry
              ? <span className="mobile-topbar-acct">{activeEntry.name || (activeEntry.number ? `#${activeEntry.number}` : 'Opening…')}</span>
              : (PAGE_LABELS[permittedActive] || 'Squish CRM')}
          </div>
          {openList.length > 0 && (
            <div className="mobile-acct-dots" aria-label="Open accounts">
              {openList.map(e => (
                <button
                  key={e.id}
                  className={`mobile-acct-dot ${e.id === activeAccountId ? 'active' : ''}`}
                  style={{ '--acct': accountColor(e.color) }}
                  onClick={() => setActive(accountRoute(e.id))}
                  aria-label={e.name || (e.number ? `#${e.number}` : 'Open account')}
                  aria-current={e.id === activeAccountId ? 'page' : undefined}
                >
                  {(e.name || '#').trim().charAt(0).toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </header>
      )}
      {isMobile && navOpen && <div className="nav-backdrop" onClick={() => setNavOpen(false)} />}

      <aside
        className={`sidebar ${navOpen ? 'is-open' : ''}`}
        style={isMobile ? undefined : { width: sidebarW, transition: 'width 0.2s ease' }}
      >

        {/* Logo */}
        <div className="sidebar-logo" style={{
          padding: collapsed ? '28px 0 24px' : '28px 24px 24px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          overflow: 'hidden',
        }}>
          <span className="logo-mark">S</span>
          {!collapsed && <>
            <span className="logo-text">quish</span>
            <span className="logo-sub">CRM</span>
          </>}
        </div>

        {/* Nav */}
        <nav className="sidebar-nav" style={{ padding: collapsed ? '16px 8px' : '16px 12px' }}>
          {NAV_ITEMS.map(item => navButton(item))}

          {/* Open accounts — one tab per account opened this session. Click to
              switch, × or middle-click to close. Closing keeps the unlock, so
              reopening from search costs no new code. */}
          {openList.length > 0 && (
            <div className="acct-tabs" aria-label="Open accounts">
              {!collapsed && <div className="acct-tabs-label">Open accounts</div>}
              {openList.map(e => {
                const isActive = e.id === activeAccountId;
                const name = e.name || (e.number ? `#${e.number}` : 'Opening…');
                return (
                  <div
                    key={e.id}
                    className={`acct-tab ${isActive ? 'active' : ''} ${collapsed ? 'collapsed' : ''}`}
                    style={{ '--acct': accountColor(e.color) }}
                  >
                    <button
                      aria-current={isActive ? 'page' : undefined}
                      className="acct-tab-open"
                      onClick={() => { setActive(accountRoute(e.id)); setNavOpen(false); }}
                      onAuxClick={ev => { if (ev.button === 1) { ev.preventDefault(); closeTab(e.id); } }}
                      title={`${name}${e.name && e.number ? ` · #${e.number}` : ''}`}
                    >
                      <span className="acct-tab-avatar">{(e.name || '#').trim().charAt(0).toUpperCase()}</span>
                      {!collapsed && (
                        <span className="acct-tab-text">
                          <span className="acct-tab-name">{name}</span>
                          {e.name && e.number && <span className="acct-tab-num">#{e.number}</span>}
                        </span>
                      )}
                    </button>
                    {!collapsed && (
                      <button className="acct-tab-close" onClick={() => closeTab(e.id)} aria-label={`Close ${name}`} title="Close">×</button>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Administration — everything only the owner opens. Pinned to the
              bottom of the nav so the daily three sit under the logo, and it
              flies OUT to the side rather than pushing the nav around. */}
          <div ref={adminRef} style={{ marginTop: 'auto', paddingTop: 12, position: 'relative' }}>
            <div style={{ height: 1, background: 'var(--border)', margin: collapsed ? '0 6px 8px' : '0 2px 8px' }} />
            <button
              className={`nav-item ${ADMIN_IDS.has(permittedActive) ? 'active' : ''}`}
              onClick={() => setAdminOpen(o => !o)}
              aria-expanded={adminOpen}
              aria-haspopup="menu"
              title={collapsed ? 'Administration' : undefined}
              style={{
                position: 'relative',
                justifyContent: collapsed ? 'center' : 'flex-start',
                padding: collapsed ? '10px 0' : '10px 14px',
                fontSize: collapsed ? undefined : 13,
                ...(ADMIN_IDS.has(permittedActive) ? {} : { color: adminOpen ? 'var(--text2)' : 'var(--text3)' }),
              }}
            >
              <span className="nav-icon">⚙</span>
              {!collapsed && <span className="nav-label">Administration</span>}
              {/* The bleed count is a money-is-leaking signal, and the panel
                  it lives in is shut by default — so it rolls up to here. */}
              {bleedCount > 0 && (collapsed
                ? <span style={dotStyle} />
                : <span style={badgeStyle}>{bleedCount}</span>)}
              {!collapsed && (
                <span style={{
                  marginLeft: bleedCount > 0 ? 6 : 'auto',
                  fontSize: 11, color: 'var(--text3)',
                }}>▸</span>
              )}
            </button>

            {/* The panel is absolutely positioned against this wrapper, not
                fixed to the viewport: the CRM body carries zoom 0.85, so
                viewport maths would need un-scaling and would drift. Bottom
                aligned because the button sits at the foot of the rail. In
                the phone drawer it opens in place instead (App.css). */}
            {adminOpen && (
              <div role="menu" className="admin-menu">
                {visibleAdminItems.map(item => navButton(item, { inPanel: true }))}
              </div>
            )}
          </div>
        </nav>

        {/* New Account button */}
        <div style={{ padding: collapsed ? '0 8px' : '0 12px', marginBottom: 12 }}>
          <button
            data-write
            onClick={() => { setActive('search'); setAutoCreate(true); setNavOpen(false); }}
            title={collapsed ? 'New Account' : undefined}
            style={{
              width: '100%', padding: collapsed ? '9px 0' : '9px 12px',
              background: 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 8, fontSize: collapsed ? 18 : 13,
              fontWeight: 600, cursor: 'pointer', textAlign: 'center',
              transition: 'all 0.2s',
            }}
          >
            {collapsed ? '+' : '+ New Account'}
          </button>
        </div>

        {/* Footer */}
        <div className="sidebar-footer" style={{ padding: collapsed ? '12px 8px' : '16px' }}>
          {collapsed ? (
            <div className="user-avatar" style={{ margin: '0 auto 12px' }} title={displayName}>{avatarInitial}</div>
          ) : (
            <div className="user-badge" style={{ marginBottom: 12 }}>
              <div className="user-avatar">{avatarInitial}</div>
              <div className="user-info">
                <div className="user-name" title={me?.email || undefined}>{displayName}</div>
                <div className="user-role">{displayRole}</div>
              </div>
            </div>
          )}
          {readOnly && (
            <div
              className="view-only-badge"
              title={isViewer(me)
                ? 'You can open and read any account. You cannot change anything.'
                : 'This login has no CRM role: it cannot open customer accounts or change anything.'}
              style={collapsed ? { padding: '3px 0', fontSize: 9 } : undefined}
            >
              {/* "View only" would be a promise to someone who cannot open an
                  account at all (no role, or a profile nobody mapped). */}
              {isViewer(me) ? (collapsed ? 'VIEW' : 'View only') : (collapsed ? 'NONE' : 'No role')}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexDirection: collapsed ? 'column' : 'row' }}>
            <button
              onClick={() => setCollapsed(p => !p)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
              style={{
                flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '7px 0', color: 'var(--text3)',
                fontSize: 13, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              {collapsed ? '»' : '«'}
            </button>
            <button
              onClick={() => setTheme(t => (t === 'dark' ? 'light' : 'dark'))}
              title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
              style={{
                flex: 1, background: 'var(--bg3)', border: '1px solid var(--border)',
                borderRadius: 7, padding: '7px 0', color: 'var(--text3)',
                fontSize: 13, cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
          </div>
        </div>

      </aside>

      <main className="main-content" style={isMobile ? undefined : { marginLeft: sidebarW, transition: 'margin-left 0.2s ease' }}>
        {!activeAccountId && pages[permittedActive]}
        {mountedIds.map(id => (
          <div
            key={id}
            className="acct-view"
            style={{
              '--acct': accountColor(openList.find(e => e.id === id)?.color),
              ...(id === activeAccountId ? {} : { display: 'none' }),
            }}
            data-view-only-account={isViewOnlyUnlock(id) ? 'true' : undefined}
          >
            {isViewOnlyUnlock(id) && !readOnly && (
              <div className="view-only-banner" role="status">
                <span>You opened this account <b>view-only</b>. Nothing can be changed until the customer confirms.</span>
                <button onClick={() => unlockForChanges(id)}>Unlock to make changes</button>
              </div>
            )}
            <CustomerDetail
              accountId={id}
              active={id === activeAccountId}
              onBack={() => setActive('search')}
              onLoaded={meta => updateOpenAccount(id, meta)}
              onNavigateToTicket={handleNavigateToTicket}
            />
          </div>
        ))}
      </main>

      {blockedNotice && (
        <div className="view-only-toast" role="status" onClick={() => setBlockedNotice(false)}>
          {VIEW_ONLY_MESSAGE}
        </div>
      )}

      {confirmAsk && (() => {
        const entry = openList.find(e => e.id === confirmAsk.id);
        return (
          <AccountConfirmModal
            name={entry?.name}
            number={entry?.number}
            color={accountColor(entry?.color)}
            idleMs={confirmAsk.idleMs}
            onConfirm={confirmYes}
            onCancel={confirmNo}
          />
        );
      })()}

      {otpTarget && (
        <OTPModal
          key={otpTarget.id}
          me={me}
          accountId={otpTarget.id}
          accountName={otpTarget.name}
          onVerified={handleOTPVerified}
          onCancel={handleOTPCancel}
        />
      )}
    </div>
  );
}